import { ApiCall } from "../../../helper/axios";

export const mergeHotelAdditionalData = (hotel, additionalData) => {
  if (!hotel || !additionalData) {
    return hotel;
  }
  const mergeHotelDetails = (existing = {}, additional = {}) => {
    const existingAmenities = Array.isArray(existing?.amenities) ? existing?.amenities : [];
    const additionalAmenities = Array.isArray(additional?.amenities) ? additional?.amenities : [];
    const amenitiesMap = new Map();
    [...additionalAmenities, ...existingAmenities].forEach(amenity => {
      const key = amenity?.id || amenity?.name;
      if (key && !amenitiesMap.has(key)) {
        amenitiesMap.set(key, amenity);
      }
    });
    const mergedAmenities = Array.from(amenitiesMap.values());
    
    return {
      ...existing,
      ...additional,
      amenities: mergedAmenities.length > 0 ? mergedAmenities : (additional?.amenities || existing?.amenities || []),
    };
  };
  let details = mergeHotelDetails(hotel?.details, additionalData);
  let mergedHotel = { ...hotel, details };
  return mergedHotel;
};

export const useMergedHotelData = (hotel, additionalData, loading) => {
  const mergedHotel = mergeHotelAdditionalData(hotel, additionalData);
  return {
    hotel: mergedHotel,
    hasAdditionalData: !!additionalData && Object.keys(additionalData).length > 0,
    loading,
    originalHotel: hotel
  };
};

// Short-lived dedup for the search-header call: identical (city, hotel) lookups
// fired close together (StrictMode double-mount, quick re-renders, multiple hook
// instances) collapse into ONE network request instead of hitting the API twice.
const _headerCache = new Map();    // key -> { ts, data }
const _headerInflight = new Map(); // key -> Promise
const _HEADER_TTL = 5000;

export const getHeaders = async (searchData,hid,cid,api=false) => {
  // Label the client already has from the selected option. Used when the DB
  // has no record for a live-API city/hotel, so the field never shows empty.
  const fallback = searchData
    ? {
        value: searchData?.code ?? cid ?? hid,
        label: searchData?.name ?? searchData?.fullRegionName,
        fullRegionName: searchData?.fullRegionName ?? searchData?.name,
        searchFor: searchData?.fullRegionName ?? searchData?.name,
        isCity: !!searchData?.isCity,
        details: { ...searchData },
      }
    : null;

  const key = `${cid || ""}_${hid || ""}`;
  const cached = _headerCache.get(key);
  if (cached && Date.now() - cached.ts < _HEADER_TTL) return cached.data;
  if (_headerInflight.has(key)) return _headerInflight.get(key);

  const request = (async () => {
    let resolved;
    try {
      const payload = { city: cid, hotel: hid };
      const response = await ApiCall("POST", "/hotels/search-header", payload);
      const data = response?.data?.data;
      // DB miss (empty) → keep the client's own selection instead of blanking.
      resolved = (data && Object.keys(data).length) ? data : (fallback ?? {});
    } catch (e) {
      resolved = fallback ?? {};
    }
    _headerCache.set(key, { ts: Date.now(), data: resolved });
    return resolved;
  })();
  _headerInflight.set(key, request);
  try {
    return await request;
  } finally {
    _headerInflight.delete(key);
  }
}
